import React from "react";
import "./info-strip.scss";

export interface InfoStripItem {
  label: string | React.ReactNode;
  key?: string;
}

export interface InfoStripProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  items: (string | React.ReactNode | InfoStripItem)[];
  separator?: React.ReactNode;
  backgroundColor?: string;
  textColor?: string;
  dotColor?: string;
  dotSize?: number | string;
  className?: string;
}

const DefaultDot = ({
  color,
  size,
}: {
  color?: string;
  size?: number | string;
}) => {
  const resolved = typeof size === "number" ? `${size}px` : size;
  return (
    <span
      className="info-strip__dot"
      style={{
        ...(color ? { backgroundColor: color } : {}),
        ...(resolved ? { width: resolved, height: resolved } : {}),
      }}
      aria-hidden="true"
    />
  );
};

const resolveItem = (
  item: string | React.ReactNode | InfoStripItem
): { node: React.ReactNode; key: string | undefined } => {
  if (item != null && typeof item === "object" && "label" in item) {
    const typed = item as InfoStripItem;
    return { node: typed.label, key: typed.key };
  }
  return { node: item as React.ReactNode, key: undefined };
};

const InfoStrip = ({
  items,
  separator,
  backgroundColor,
  textColor,
  dotColor,
  dotSize,
  className,
  style,
  ...rest
}: InfoStripProps) => {
  if (!items || items.length === 0) return null;

  const rootStyle: React.CSSProperties = {
    ...(backgroundColor ? { backgroundColor } : {}),
    ...style,
  };

  const hasCustomSeparator = separator !== undefined;

  return (
    <div
      className={["info-strip", className].filter(Boolean).join(" ")}
      style={rootStyle}
      {...rest}
    >
      {items.map((item, index) => {
        const { node, key } = resolveItem(item);
        return (
          <React.Fragment key={key ?? index}>
            <span
              className="info-strip__item"
              style={textColor ? { color: textColor } : undefined}
            >
              {node}
            </span>
            {index < items.length - 1 &&
              (hasCustomSeparator ? (
                <span className="info-strip__separator" aria-hidden="true">
                  {separator}
                </span>
              ) : (
                <DefaultDot color={dotColor} size={dotSize} />
              ))}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export { InfoStrip };
export default InfoStrip;
